import { create } from "zustand";

export const LOCAL_WORKSPACE_ID = "local";
export const MANAGED_CLOUD_WORKSPACE_ID = "managed_cloud";
export const PRIVATE_WORKSPACE_ID = "private_workspace";
const BROWSER_SELECTED_WORKSPACE_STORAGE_KEY = "magnis-selected-workspace";

export type WorkspaceAuthMethod = "open" | "google";
export type WorkspaceKind = "local" | "managed_cloud" | "private_preview";

export interface WorkspaceDefinition {
  readonly id: string;
  readonly label: string;
  readonly kind: WorkspaceKind;
  readonly apiBaseUrl: string | null;
  readonly authMethod: WorkspaceAuthMethod | null;
}

interface WorkspaceConfigResponse {
  readonly selected_workspace_id: string;
  readonly workspaces: readonly WorkspaceConfigEntry[];
}

interface WorkspaceConfigEntry {
  readonly id: string;
  readonly label: string;
  readonly kind: WorkspaceKind;
  readonly api_base_url: string | null;
  readonly auth_method: WorkspaceAuthMethod | null;
}

function readBrowserSelectedWorkspaceId(): string | null {
  try {
    return localStorage.getItem(BROWSER_SELECTED_WORKSPACE_STORAGE_KEY);
  } catch {
    return null;
  }
}

function persistBrowserSelectedWorkspaceId(workspaceId: string): void {
  try {
    localStorage.setItem(BROWSER_SELECTED_WORKSPACE_STORAGE_KEY, workspaceId);
  } catch {
    /* storage unavailable — keep runtime selection only */
  }
}

function resolveSelectedWorkspaceId(
  workspaces: readonly WorkspaceConfigEntry[],
  defaultWorkspaceId: string,
): string {
  const storedWorkspaceId = readBrowserSelectedWorkspaceId();
  if (storedWorkspaceId == null) {
    return defaultWorkspaceId;
  }
  return workspaces.some((workspace) => workspace.id === storedWorkspaceId)
    ? storedWorkspaceId
    : defaultWorkspaceId;
}

interface WorkspaceState {
  readonly ready: boolean;
  readonly workspaces: readonly WorkspaceDefinition[];
  readonly selectedWorkspaceId: string | null;
  initialize: () => Promise<void>;
  selectWorkspace: (workspaceId: string) => Promise<void>;
}

function isTauriRuntime(): boolean {
  return "__TAURI__" in window || "__TAURI_INTERNALS__" in window;
}

function isLocalHostname(hostname: string): boolean {
  if (hostname === "localhost" || hostname === "127.0.0.1") return true;
  const extra =
    typeof import.meta.env.VITE_LOCAL_HOSTNAMES === "string"
      ? import.meta.env.VITE_LOCAL_HOSTNAMES
      : "";
  if (!extra) return false;
  return extra
    .split(",")
    .map((s) => s.trim())
    .filter((s) => s.length > 0)
    .includes(hostname);
}

export function resolveApiBaseUrl(rawApiUrl: string | undefined, pageUrl: URL): string {
  const configured = new URL(rawApiUrl ?? pageUrl.origin);

  if (
    isLocalHostname(configured.hostname) &&
    isLocalHostname(pageUrl.hostname) &&
    configured.hostname !== pageUrl.hostname
  ) {
    configured.hostname = pageUrl.hostname;
  }

  return configured.toString().replace(/\/$/, "");
}

function resolveBrowserApiUrl(): string {
  const configuredApiUrl =
    typeof import.meta.env.VITE_API_URL === "string" &&
    import.meta.env.VITE_API_URL.length > 0
      ? import.meta.env.VITE_API_URL
      : window.location.origin;
  return resolveApiBaseUrl(configuredApiUrl, new URL(window.location.href));
}

export function resolveManagedCloudApiBaseUrl(): string {
  if (
    typeof import.meta.env.VITE_MANAGED_CLOUD_API_URL === "string" &&
    import.meta.env.VITE_MANAGED_CLOUD_API_URL.length > 0
  ) {
    return import.meta.env.VITE_MANAGED_CLOUD_API_URL;
  }
  if (window.location.hostname.endsWith("magnis.ai")) {
    return resolveBrowserApiUrl();
  }
  return "https://api.magnis.ai";
}

function buildBrowserWorkspaceConfig(): WorkspaceConfigResponse {
  const browserApiUrl = resolveBrowserApiUrl();
  const managedCloudApiUrl = resolveManagedCloudApiBaseUrl();
  const browserUrl = new URL(browserApiUrl, window.location.origin);
  const localSelected = isLocalHostname(browserUrl.hostname);
  const workspaces: WorkspaceConfigEntry[] = [];

  if (localSelected) {
    workspaces.push({
      id: LOCAL_WORKSPACE_ID,
      label: "Local Workspace",
      kind: "local",
      api_base_url: browserApiUrl,
      auth_method: "open",
    });
  }

  workspaces.push(
    {
      id: MANAGED_CLOUD_WORKSPACE_ID,
      label: "Magnis Cloud (magnis.ai)",
      kind: "managed_cloud",
      api_base_url: managedCloudApiUrl,
      auth_method: "google",
    },
    {
      id: PRIVATE_WORKSPACE_ID,
      label: "Connect Private Workspace",
      kind: "private_preview",
      api_base_url: null,
      auth_method: null,
    },
  );

  const defaultWorkspaceId = localSelected
    ? LOCAL_WORKSPACE_ID
    : MANAGED_CLOUD_WORKSPACE_ID;

  return {
    selected_workspace_id: resolveSelectedWorkspaceId(
      workspaces,
      defaultWorkspaceId,
    ),
    workspaces,
  };
}

function normalizeWorkspaces(
  input: WorkspaceConfigResponse,
): readonly WorkspaceDefinition[] {
  return input.workspaces.map((workspace) => ({
    id: workspace.id,
    label: workspace.label,
    kind: workspace.kind,
    apiBaseUrl: workspace.api_base_url,
    authMethod: workspace.auth_method,
  }));
}

async function loadTauriWorkspaceConfig(): Promise<WorkspaceConfigResponse> {
  const { invoke } = await import("@tauri-apps/api/core");
  return invoke<WorkspaceConfigResponse>("get_workspace_config");
}

async function persistTauriWorkspaceSelection(
  workspaceId: string,
): Promise<void> {
  const { invoke } = await import("@tauri-apps/api/core");
  await invoke("set_selected_workspace", { workspaceId });
}

export const useWorkspaceStore = create<WorkspaceState>((set, get) => ({
  ready: false,
  workspaces: [],
  selectedWorkspaceId: null,

  initialize: async (): Promise<void> => {
    if (get().ready) return;
    const config = isTauriRuntime()
      ? await loadTauriWorkspaceConfig()
      : buildBrowserWorkspaceConfig();
    set({
      ready: true,
      selectedWorkspaceId: config.selected_workspace_id,
      workspaces: normalizeWorkspaces(config),
    });
  },

  selectWorkspace: async (workspaceId: string): Promise<void> => {
    const workspace = get().workspaces.find((entry) => entry.id === workspaceId);
    if (!workspace || workspace.kind === "private_preview") {
      throw new Error(`Unsupported workspace selection: ${workspaceId}`);
    }
    if (workspaceId === get().selectedWorkspaceId) return;

    if (isTauriRuntime()) {
      await persistTauriWorkspaceSelection(workspaceId);
    } else {
      persistBrowserSelectedWorkspaceId(workspaceId);
    }
    set({ selectedWorkspaceId: workspaceId });
  },
}));

export function getCurrentWorkspace(): WorkspaceDefinition | null {
  const state = useWorkspaceStore.getState();
  return (
    state.workspaces.find(
      (workspace) => workspace.id === state.selectedWorkspaceId,
    ) ?? null
  );
}

export function getWorkspaceById(workspaceId: string): WorkspaceDefinition | null {
  const state = useWorkspaceStore.getState();
  return state.workspaces.find((workspace) => workspace.id === workspaceId) ?? null;
}

export function getApiBaseUrl(): string {
  return getCurrentWorkspace()?.apiBaseUrl ?? resolveBrowserApiUrl();
}

export function getManagedCloudApiBaseUrl(): string {
  return (
    getWorkspaceById(MANAGED_CLOUD_WORKSPACE_ID)?.apiBaseUrl ??
    resolveManagedCloudApiBaseUrl()
  );
}
